/**
 * Products.jsx — Redesigned · Dark Luxury Fintech
 * Accent: teal/emerald · Drawer latéral pour Add/Edit
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase/config";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { Package, Search, Plus, Edit2, Trash2, RefreshCw, X, Check, Home, AlertTriangle, Image as ImageIcon, Tag, Box, ChevronDown, Filter, Star, Grid } from "react-feather";
import GenerateProductQR from "./Products/GenerateProductQR";
import FormatPrice from "../components/FormatPrice";
// ─── Static data ──────────────────────────────────────────────────────────
const PRODUCT_TYPES = ["Camera","Smartphone","Laptop","Tablet","Smartwatch","Router","Smart Home Device","Accessory","Television","Audio Equipment","Networking Equipment","Wearable","Storage Device","Peripheral","Gaming Device","Power bank","Charger","Projector","Monitor","Printer","Circuit Breaker","Switch","Socket"];
const BRANDS = ["Legrand","Hager","Siemens","Wago","Schneider","Apple","Samsung","Google","Lenovo","HP","Asus","Acer","Huawei","Dell","Hisense","Hikvision","Bosch","Panasonic","Philips","Sony"];
const EMPTY = { 
  name:"", 
  description:"", 
  price:0, 
  brand:"", 
  stock:0, 
  type:"", 
  image:"", 
  image2:"", 
  image3:"", 
  videoShort:"",  // AJOUTER CETTE LIGNE
  showOnHome:false, 
  mrp:null 
};
// ─── Atoms ────────────────────────────────────────────────────────────────
const GlassPanel = ({ children, style={} }) => (
  <div style={{ background:"rgba(17,24,39,.85)", border:"1px solid rgba(255,255,255,.06)", borderRadius:16, backdropFilter:"blur(16px)", ...style }}>{children}</div>
);

const MiniStat = ({ label, value, accent="#14b8a6", icon:Icon, index=0 }) => (
  <motion.div initial={{ opacity:0, y:18 }} animate={{ opacity:1, y:0 }} transition={{ delay:index*.07, duration:.45, ease:[.22,1,.36,1] }} whileHover={{ y:-3, transition:{ duration:.15 } }}
    style={{ background:"rgba(17,24,39,.85)", border:`1px solid ${accent}33`, borderRadius:14, padding:"14px 16px", boxShadow:`0 0 24px ${accent}0d`, position:"relative", overflow:"hidden" }}>
    <div style={{ position:"absolute", top:-24, right:-24, width:72, height:72, borderRadius:"50%", background:`${accent}18`, filter:"blur(20px)", pointerEvents:"none" }} />
    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", position:"relative" }}>
      <div>
        <p style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".14em", color:"#475569", marginBottom:8 }}>{label}</p>
        <p style={{ fontSize:22, fontWeight:700, color:"#fff", lineHeight:1 }}>{value}</p>
      </div>
      {Icon && <div style={{ width:32, height:32, borderRadius:9, background:`${accent}18`, border:`1px solid ${accent}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon size={15} color={accent} strokeWidth={1.8} /></div>}
    </div>
    <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }} transition={{ duration:.5, delay:.2+index*.07 }} style={{ position:"absolute", bottom:0, left:0, right:0, height:2, background:accent, opacity:.5, transformOrigin:"left" }} />
  </motion.div>
);

const Btn = ({ children, onClick, variant="ghost", size="sm", disabled=false, style={}, type="button" }) => {
  const V = { ghost:{ background:"rgba(255,255,255,.06)", color:"#cbd5e1", border:"1px solid rgba(255,255,255,.08)" }, teal:{ background:"rgba(20,184,166,.15)", color:"#2dd4bf", border:"1px solid rgba(20,184,166,.3)" }, red:{ background:"rgba(239,68,68,.15)", color:"#f87171", border:"1px solid rgba(239,68,68,.3)" } };
  const v = V[variant]||V.ghost;
  const pad = size==="xs"?"3px 8px":size==="sm"?"5px 12px":"8px 18px";
  return <button type={type} onClick={onClick} disabled={disabled} style={{ ...v, padding:pad, borderRadius:8, fontSize:11, fontWeight:600, cursor:disabled?"not-allowed":"pointer", opacity:disabled?.45:1, display:"inline-flex", alignItems:"center", gap:5, transition:"opacity .15s", whiteSpace:"nowrap", ...style }}>{children}</button>;
};

const Field = ({ label, children }) => (
  <div>
    {label && <label style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".12em", color:"#475569", display:"block", marginBottom:5 }}>{label}</label>}
    {children}
  </div>
);

const DI = ({ value, onChange, placeholder, type="text", style={} }) => (
  <input type={type} value={value||""} onChange={onChange} placeholder={placeholder}
    style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"8px 12px", color:"#e2e8f0", fontSize:12, width:"100%", outline:"none", ...style }} />
);

const DS = ({ value, onChange, children }) => (
  <select value={value||""} onChange={onChange}
    style={{ background:"#111827", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"8px 12px", color:value?"#e2e8f0":"#475569", fontSize:12, width:"100%", outline:"none", appearance:"none" }}>
    {children}
  </select>
);

const StockBadge = ({ stock }) => {
  if (stock<=0) return <span style={{ background:"rgba(239,68,68,.12)", color:"#f87171", boxShadow:"inset 0 0 0 1px rgba(239,68,68,.3)", fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20 }}>Out of stock</span>;
  if (stock<=5) return <span style={{ background:"rgba(245,158,11,.12)", color:"#fbbf24", boxShadow:"inset 0 0 0 1px rgba(245,158,11,.3)", fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20 }}>Low: {stock}</span>;
  return <span style={{ background:"rgba(16,185,129,.12)", color:"#34d399", boxShadow:"inset 0 0 0 1px rgba(16,185,129,.3)", fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20 }}>{stock} in stock</span>;
};

const ProductThumb = ({ src, size=44 }) => (
  <div style={{ width:size, height:size, borderRadius:10, overflow:"hidden", flexShrink:0, border:"1px solid rgba(255,255,255,.08)", background:"rgba(255,255,255,.04)", display:"flex", alignItems:"center", justifyContent:"center" }}>
    {src ? <img src={src} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e=>{e.target.style.display="none"}} /> : <ImageIcon size={16} color="#334155" />}
  </div>
);

const Toggle = ({ value, onChange }) => (
  <div onClick={onChange} style={{ width:36, height:20, borderRadius:10, background:value?"#14b8a6":"rgba(255,255,255,.1)", position:"relative", transition:"background .2s", flexShrink:0, cursor:"pointer" }}>
    <motion.div animate={{ x:value?18:2 }} transition={{ type:"spring", stiffness:500, damping:30 }} style={{ position:"absolute", top:2, width:16, height:16, borderRadius:"50%", background:"#fff", boxShadow:"0 1px 4px rgba(0,0,0,.3)" }} />
  </div>
);

const ConfirmDialog = ({ open, onConfirm, onCancel }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.7)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, zIndex:60 }}>
        <motion.div initial={{ scale:.92, y:10 }} animate={{ scale:1, y:0 }} exit={{ scale:.92 }} style={{ background:"#0d1526", border:"1px solid rgba(255,255,255,.12)", borderRadius:16, padding:28, maxWidth:360, width:"100%", textAlign:"center" }}>
          <AlertTriangle size={28} color="#f87171" style={{ margin:"0 auto 12px" }} />
          <p style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", marginBottom:8 }}>Delete Product?</p>
          <p style={{ fontSize:12, color:"#64748b", marginBottom:22, lineHeight:1.6 }}>This action is permanent and cannot be undone.</p>
          <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
            <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
            <Btn variant="red" onClick={onConfirm}><Trash2 size={12}/> Delete</Btn>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Drawer ───────────────────────────────────────────────────────────────
const ProductDrawer = ({ open, onClose, form, setForm, onSubmit, submitting, isEdit }) => {
  const set = key => e => setForm(p => ({ ...p, [key]: e.target.type==="checkbox"?e.target.checked:e.target.value }));
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", backdropFilter:"blur(4px)", zIndex:40 }} onClick={onClose} />
          <motion.div initial={{ x:"100%" }} animate={{ x:0 }} exit={{ x:"100%" }} transition={{ type:"spring", stiffness:320, damping:38 }}
            style={{ position:"fixed", top:0, right:0, bottom:0, width:"100%", maxWidth:460, background:"#0d1526", borderLeft:"1px solid rgba(255,255,255,.1)", zIndex:50, display:"flex", flexDirection:"column" }}>
            {/* Header */}
            <div style={{ padding:"16px 20px", borderBottom:"1px solid rgba(255,255,255,.06)", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
              <div>
                <p style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".16em", color:"rgba(45,212,191,.7)", marginBottom:3 }}>{isEdit?"Editing":"New Product"}</p>
                <h3 style={{ fontSize:14, fontWeight:700, color:"#fff" }}>{isEdit?form.name||"Edit Product":"Add Product"}</h3>
              </div>
              <button onClick={onClose} style={{ background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.08)", borderRadius:8, padding:"5px 8px", cursor:"pointer", color:"#94a3b8", display:"flex" }}><X size={14}/></button>
            </div>

            {/* Body */}
            <div style={{ flex:1, overflowY:"auto", padding:20 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Product Name *">
                <DI value={form.name} onChange={set("name")} placeholder="e.g. Samsung Galaxy S25" />
              </Field>

              <Field label="Description">
                <textarea
                  value={form.description || ""}
                  onChange={set("description")}
                  placeholder="Brief product description…"
                  rows={3}
                  style={{
                    background: "rgba(255,255,255,.04)",
                    border: "1px solid rgba(255,255,255,.1)",
                    borderRadius: 10,
                    padding: "8px 12px",
                    color: "#e2e8f0",
                    fontSize: 12,
                    width: "100%",
                    outline: "none",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                />
              </Field>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="Price (π) *">
                  <DI type="number" value={form.price} onChange={set("price")} placeholder="0.00" />
                </Field>
                <Field label="MRP (optional)">
                  <DI type="number" value={form.mrp || ""} onChange={set("mrp")} placeholder="0.00" />
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="Brand">
                  <DS value={form.brand} onChange={set("brand")}>
                    <option value="">Select brand…</option>
                    {BRANDS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </DS>
                </Field>
                <Field label="Stock">
                  <DI type="number" value={form.stock} onChange={set("stock")} placeholder="0" />
                </Field>
              </div>

              <Field label="Product Type">
                <DS value={form.type} onChange={set("type")}>
                  <option value="">Select type…</option>
                  {PRODUCT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </DS>
              </Field>

              {/* Images section */}
              <div style={{ borderTop: "1px solid rgba(255,255,255,.05)", paddingTop: 14 }}>
                <p
                  style={{
                    fontSize: 9,
                    textTransform: "uppercase",
                    letterSpacing: ".14em",
                    color: "#334155",
                    marginBottom: 10,
                  }}
                >
                  Images & Media
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <Field label="Main Image URL">
                    <DI value={form.image} onChange={set("image")} placeholder="https://…" />
                  </Field>
                  <Field label="Image 2 (optional)">
                    <DI value={form.image2} onChange={set("image2")} placeholder="https://…" />
                  </Field>
                  <Field label="Image 3 (optional)">
                    <DI value={form.image3} onChange={set("image3")} placeholder="https://…" />
                  </Field>

                  {/* Short Video Field - NOUVEAU */}
                  <Field label="Short Video (optional)">
                    <DI
                      value={form.videoShort || ""}
                      onChange={set("videoShort")}
                      placeholder="https://… (MP4, WebM, etc.)"
                    />
                    <p
                      style={{
                        fontSize: 8,
                        color: "#475569",
                        marginTop: 4,
                      }}
                    >
                      🎬 Recommandé: vidéo courte &lt; 10 MB, format MP4
                    </p>
                  </Field>
                </div>

                {/* Preview thumbnails + video preview */}
                {((form.image || form.image2 || form.image3) || form.videoShort) && (
                  <div style={{ marginTop: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Images preview */}
                      {[form.image, form.image2, form.image3]
                        .filter(Boolean)
                        .map((url, i) => (
                          <div
                            key={i}
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 10,
                              overflow: "hidden",
                              border: "1px solid rgba(255,255,255,.08)",
                              flexShrink: 0,
                              background: "rgba(255,255,255,.04)",
                            }}
                          >
                            <img
                              src={url}
                              alt={`preview ${i + 1}`}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              onError={(e) => (e.target.style.display = "none")}
                            />
                          </div>
                        ))}

                      {/* Video preview - NOUVEAU */}
                      {form.videoShort && (
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: 10,
                            overflow: "hidden",
                            border: "1px solid rgba(45,212,191,.4)",
                            flexShrink: 0,
                            background: "#000",
                            position: "relative",
                          }}
                        >
                          <video
                            src={form.videoShort}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            preload="metadata"
                            muted
                          />
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "rgba(0,0,0,.3)",
                              pointerEvents: "none",
                            }}
                          >
                            <div
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                background: "rgba(255,255,255,.8)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span style={{ fontSize: 10, marginLeft: 1 }}>▶</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Media counter - NOUVEAU */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 8,
                        padding: "4px 8px",
                        background: "rgba(45,212,191,.1)",
                        borderRadius: 8,
                        width: "fit-content",
                      }}
                    >
                      <span style={{ fontSize: 10, color: "#2dd4bf" }}>📦</span>
                      <span style={{ fontSize: 10, color: "#94a3b8" }}>
                        {[form.image, form.image2, form.image3].filter(Boolean).length} image
                        {[form.image, form.image2, form.image3].filter(Boolean).length > 1 ? "s" : ""}
                        {form.videoShort && (
                          <>
                            {" "}
                            + 1 vidéo courte
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Show on home toggle */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: "rgba(20,184,166,.06)",
                  border: "1px solid rgba(20,184,166,.15)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  cursor: "pointer",
                }}
              >
                <Toggle
                  value={form.showOnHome}
                  onChange={() => setForm((p) => ({ ...p, showOnHome: !p.showOnHome }))}
                />
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0" }}>Show on Homepage</p>
                  <p style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>
                    Feature this product on the storefront
                  </p>
                </div>
              </label>
            </div>
            </div>

            {/* Footer */}
            <div style={{ padding:"14px 20px", borderTop:"1px solid rgba(255,255,255,.06)", display:"flex", gap:10, flexShrink:0 }}>
              <Btn variant="ghost" onClick={onClose} style={{ flex:1, justifyContent:"center" }}>Cancel</Btn>
              <Btn variant="teal" onClick={onSubmit} disabled={submitting} style={{ flex:2, justifyContent:"center" }}>
                {submitting
                  ? <><div style={{ width:12, height:12, borderRadius:"50%", border:"2px solid rgba(45,212,191,.4)", borderTopColor:"#2dd4bf", animation:"spin .7s linear infinite" }}/>{isEdit?"Saving…":"Adding…"}</>
                  : isEdit?<><Check size={13}/>Save Changes</>:<><Plus size={13}/>Add Product</>
                }
              </Btn>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
const Products = () => {
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [searchTerm, setSearchTerm]   = useState("");
  const [filterType, setFilterType]   = useState("all");
  const [filterBrand, setFilterBrand] = useState("all");
  const [filterHome, setFilterHome]   = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [form, setForm]               = useState(EMPTY);
  const [editingId, setEditingId]     = useState(null);
  const [confirmId, setConfirmId]     = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedProductForQR, setSelectedProductForQR] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try { setProducts((await getDocs(collection(db,"products"))).docs.map(d=>({id:d.id,...d.data()}))); }
    catch { toast.error("Failed to fetch products"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd  = () => { setForm(EMPTY); setEditingId(null); setDrawerOpen(true); };
  const openEdit = p  => { const {id,...rest}=p; setForm({...EMPTY,...rest}); setEditingId(id); setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); setEditingId(null); };

  const handleSubmit = async () => {
    if (!form.name.trim())            { toast.error("Product name is required"); return; }
    if (!form.price||form.price<=0)   { toast.error("Valid price is required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, price:Number(form.price), stock:Number(form.stock), mrp:form.mrp?Number(form.mrp):null, updatedAt:new Date().toISOString() };
      if (editingId) {
        const {id:_,...data}=payload;
        await updateDoc(doc(db,"products",editingId), data);
        toast.success("Product updated!");
      } else {
        await addDoc(collection(db,"products"), { ...payload, createdAt:new Date().toISOString() });
        toast.success("Product added!");
      }
      closeDrawer(); fetchProducts();
    } catch { toast.error(editingId?"Failed to update":"Failed to add"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteConfirm = async () => {
    try { await deleteDoc(doc(db,"products",confirmId)); setProducts(p=>p.filter(x=>x.id!==confirmId)); toast.success("Product deleted!"); }
    catch { toast.error("Failed to delete"); }
    finally { setConfirmId(null); }
  };

  const filtered = useMemo(() => {
    let f=[...products];
    if (searchTerm.trim()) { const s=searchTerm.toLowerCase(); f=f.filter(p=>p.name?.toLowerCase().includes(s)||p.brand?.toLowerCase().includes(s)||p.type?.toLowerCase().includes(s)); }
    if (filterType!=="all")  f=f.filter(p=>p.type===filterType);
    if (filterBrand!=="all") f=f.filter(p=>p.brand===filterBrand);
    if (filterHome==="yes")  f=f.filter(p=>p.showOnHome);
    if (filterHome==="no")   f=f.filter(p=>!p.showOnHome);
    return f;
  }, [products,searchTerm,filterType,filterBrand,filterHome]);

  const stats = useMemo(() => ({
    total:     products.length,
    inStock:   products.filter(p=>(p.stock||0)>0).length,
    outOfStock:products.filter(p=>(p.stock||0)<=0).length,
    onHome:    products.filter(p=>p.showOnHome).length,
  }), [products]);

  const hasFilters = filterType!=="all"||filterBrand!=="all"||filterHome!=="all"||searchTerm;
  const clearFilters = () => { setSearchTerm(""); setFilterType("all"); setFilterBrand("all"); setFilterHome("all"); };
  const COL = "52px 1fr 100px 90px 80px 110px 80px 90px";

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} style={{ paddingBottom:48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:.5}50%{opacity:1}} input::placeholder,textarea::placeholder{color:#334155}`}</style>

      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:18 }}>
          <div>
            <p style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".18em", color:"violet", marginBottom:4 }}>Management</p>
            <h1 style={{ fontSize:22, fontWeight:800, color:"#fff", display:"flex", alignItems:"center", gap:10, marginBottom:3 }}>
              <Package size={20} color="violet" strokeWidth={2}/> Products
            </h1>
            <p style={{ fontSize:11, color:"#475569" }}>Manage your Pi Store product catalogue</p>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <Btn variant="ghost" onClick={fetchProducts} disabled={loading}>
              <RefreshCw size={13} style={{ animation:loading?"spin .8s linear infinite":"none" }}/>{loading?"Refreshing…":"Refresh"}
            </Btn>
            <Btn variant="teal" onClick={openAdd}><Plus size={13}/> Add Product</Btn>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12 }}>
          <MiniStat label="Total Products" value={stats.total}      accent="#2dd4bf" icon={Package} index={0}/>
          <MiniStat label="In Stock"       value={stats.inStock}    accent="#34d399" icon={Box}     index={1}/>
          <MiniStat label="Out of Stock"   value={stats.outOfStock} accent="#f87171" icon={Tag}     index={2}/>
          <MiniStat label="On Homepage"    value={stats.onHome}     accent="#f59e0b" icon={Star}    index={3}/>
        </div>
      </div>

      {/* Filters */}
      <GlassPanel style={{ marginBottom:14 }}>
        <button onClick={()=>setFiltersOpen(v=>!v)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", padding:"12px 16px", background:"none", border:"none", cursor:"pointer", color:"#e2e8f0" }}>
          <span style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, fontWeight:600 }}>
            <Filter size={13} color="#2dd4bf"/> Filters &amp; Search
            {hasFilters&&<span style={{ background:"rgba(45,212,191,.2)", color:"#2dd4bf", fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:20, border:"1px solid rgba(45,212,191,.3)" }}>ACTIVE</span>}
          </span>
          <ChevronDown size={13} style={{ transform:filtersOpen?"rotate(180deg)":"none", transition:"transform .2s", color:"#475569" }}/>
        </button>
        <AnimatePresence>
          {filtersOpen&&(
            <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:.2 }} style={{ overflow:"hidden" }}>
              <div style={{ padding:"0 16px 16px", borderTop:"1px solid rgba(255,255,255,.05)" }}>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginTop:14 }}>
                  <div>
                    <label style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".12em", color:"#475569", display:"block", marginBottom:5 }}>Search</label>
                    <div style={{ position:"relative" }}>
                      <Search size={12} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#475569", pointerEvents:"none" }}/>
                      <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="Name, brand, type…" style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"8px 12px 8px 28px", color:"#e2e8f0", fontSize:12, width:"100%", outline:"none" }}/>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".12em", color:"#475569", display:"block", marginBottom:5 }}>Type</label>
                    <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ background:"#111827", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"8px 12px", color:"#e2e8f0", fontSize:12, width:"100%", outline:"none", appearance:"none" }}>
                      <option value="all">All Types</option>
                      {PRODUCT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".12em", color:"#475569", display:"block", marginBottom:5 }}>Brand</label>
                    <select value={filterBrand} onChange={e=>setFilterBrand(e.target.value)} style={{ background:"#111827", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"8px 12px", color:"#e2e8f0", fontSize:12, width:"100%", outline:"none", appearance:"none" }}>
                      <option value="all">All Brands</option>
                      {BRANDS.map(b=><option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".12em", color:"#475569", display:"block", marginBottom:5 }}>Homepage</label>
                    <select value={filterHome} onChange={e=>setFilterHome(e.target.value)} style={{ background:"#111827", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"8px 12px", color:"#e2e8f0", fontSize:12, width:"100%", outline:"none", appearance:"none" }}>
                      <option value="all">All</option>
                      <option value="yes">On Homepage</option>
                      <option value="no">Hidden</option>
                    </select>
                  </div>
                </div>
                {hasFilters&&<div style={{ marginTop:12, display:"flex", justifyContent:"flex-end" }}><Btn variant="red" size="xs" onClick={clearFilters}><X size={11}/> Clear filters</Btn></div>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassPanel>

      {/* Table */}
      <GlassPanel>
        <div style={{ display:"grid", gridTemplateColumns:COL, gap:8, padding:"10px 16px", borderBottom:"1px solid rgba(255,255,255,.05)", fontSize:9, textTransform:"uppercase", letterSpacing:".12em", color:"#334155", fontWeight:700, alignItems:"center" }}>
          <div>Image</div><div>Product</div><div>Price</div><div>Brand</div><div>Type</div><div>Stock</div><div>Home</div><div>Actions</div>
        </div>

        {loading&&products.length===0&&(
          <div style={{ padding:"24px 16px", display:"flex", flexDirection:"column", gap:10 }}>
            {[...Array(5)].map((_,i)=><div key={i} style={{ height:48, borderRadius:10, background:"rgba(255,255,255,.04)", animation:`pulse 1.5s ease-in-out ${i*.1}s infinite` }}/>)}
          </div>
        )}

        {!loading&&filtered.length===0&&(
          <div style={{ padding:"48px 24px", textAlign:"center" }}>
            <Package size={32} style={{ margin:"0 auto 12px", opacity:.2, display:"block", color:"#475569" }}/>
            <p style={{ fontSize:13, fontWeight:600, color:"#334155" }}>No products found</p>
            {hasFilters
              ? <p style={{ fontSize:11, marginTop:6, color:"#1e293b" }}>Try clearing the filters</p>
              : <div style={{ marginTop:14 }}><Btn variant="teal" onClick={openAdd}><Plus size={12}/> Add your first product</Btn></div>
            }
          </div>
        )}

        {filtered.map((p,idx)=>(
          <motion.div key={p.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:Math.min(idx*.02,.3) }}
            style={{ display:"grid", gridTemplateColumns:COL, gap:8, padding:"11px 16px", borderBottom:"1px solid rgba(255,255,255,.03)", alignItems:"center", transition:"background .15s" }}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(20,184,166,.03)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}
          >
            {/* COLONNE IMAGE */}
            <ProductThumb src={p.image} size={44}/>

            {/* COLONNE PRODUCT (nom + infos média) */}
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                  <div style={{ display:"flex", gap:3, alignItems:"center" }}>
                    {p.image2 && <span style={{ fontSize:9, color:"#2dd4bf" }}>📷+1</span>}
                    {p.image3 && <span style={{ fontSize:9, color:"#2dd4bf" }}>📷+2</span>}
                    {p.videoShort && <span style={{ fontSize:9, color:"#f59e0b" }}>🎬 vidéo</span>}
                  </div>
                  <span style={{ fontSize:8, color:"#475569" }}>
                    {[p.image, p.image2, p.image3].filter(Boolean).length} image(s)
                    {p.videoShort && " + vidéo"}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#e2e8f0" }}>{p.name}</span>
              </div>
              {p.description && (
                <p style={{ fontSize: 10, color: "#334155", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                  {p.description}
                </p>
              )}
            </div>

            {/* COLONNE PRICE */}
            <div>
              <p style={{ fontSize:12, fontWeight:700, color:"#fff" }}>{FormatPrice(p.price)}</p>
              {p.mrp && p.mrp > p.price && (
                <p style={{ fontSize:9, color:"#475569", textDecoration:"line-through", marginTop:1 }}>{FormatPrice(p.mrp)}</p>
              )}
            </div>

            {/* COLONNE BRAND */}
            <p style={{ fontSize:11, color:"#94a3b8", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.brand || "—"}</p>

            {/* COLONNE TYPE */}
            <p style={{ fontSize:10, color:"#475569", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.type || "—"}</p>

            {/* COLONNE STOCK */}
            <StockBadge stock={p.stock || 0}/>

            {/* COLONNE HOME */}
            <div>
              {p.showOnHome
                ? <span style={{ background:"rgba(245,158,11,.12)", color:"#f59e0b", boxShadow:"inset 0 0 0 1px rgba(245,158,11,.3)", fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:20, display:"inline-flex", alignItems:"center", gap:3 }}><Home size={9}/>Yes</span>
                : <span style={{ background:"rgba(100,116,139,.1)", color:"#475569", fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:20 }}>No</span>
              }
            </div>

            {/* COLONNE ACTIONS */}
            <div style={{ display:"flex", gap:5 }}>
              <Btn size="xs" variant="teal" onClick={()=>openEdit(p)}><Edit2 size={10}/></Btn>
              <Btn size="xs" variant="teal" onClick={() => {
                setSelectedProductForQR(p);
                setShowQRModal(true);
              }}><Grid size={10}/></Btn>
              <Btn size="xs" variant="red" onClick={()=>setConfirmId(p.id)}><Trash2 size={10}/></Btn>
            </div>
            {/* Modal QR Code */}
            {showQRModal && selectedProductForQR && (
              <div style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.7)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 100,
                padding: "16px"
              }}>
                <GenerateProductQR
                  product={selectedProductForQR}
                  onClose={() => {
                    setShowQRModal(false);
                    setSelectedProductForQR(null);
                  }}
                />
              </div>
            )}
          </motion.div>
        ))}

        {filtered.length>0&&(
          <div style={{ padding:"10px 16px", borderTop:"1px solid rgba(255,255,255,.04)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <p style={{ fontSize:10, color:"#334155" }}>Showing <span style={{ color:"#2dd4bf", fontWeight:700 }}>{filtered.length}</span> of <span style={{ color:"#2dd4bf", fontWeight:700 }}>{products.length}</span> products</p>
            <Btn variant="teal" size="xs" onClick={openAdd}><Plus size={10}/> Add Product</Btn>
          </div>
        )}
      </GlassPanel>

      <ProductDrawer open={drawerOpen} onClose={closeDrawer} form={form} setForm={setForm} onSubmit={handleSubmit} submitting={submitting} isEdit={!!editingId}/>
      <ConfirmDialog open={!!confirmId} onConfirm={handleDeleteConfirm} onCancel={()=>setConfirmId(null)}/>
    </motion.div>
  );
};

export default Products;
